import { useState, useEffect, useContext, useMemo } from 'react'
import { HeadingTitle } from '../../../components/modules/ui/index'
import GlobalContext from '../../../context/global-context'
import { BusinessAPI } from '../../../api/business.api'
import { CentroCostosAPI } from '../../../api/centroCostos.api'
import dayjs from 'dayjs'
import FilterDateControl from '../../../components/modules/table/FilterDateControl'
import FilterControl from '../../../components/modules/table/FilterControl'
import TableExtend from './Table'
import { TablesAPI } from '../../../api/table.api'
import ExportToXlsxModal from '../../../components/modules/modals/ExportToXlsxModal'
import { currentYear, yearsOptions } from '../../../components/utils/constant'

const UNASSIGNED_CENTER_COST_VALUE = '__SIN_ASIGNAR__'

export default function ListEstadoDeResultadosExtend() {
  const { ui, currentBusiness } = useContext(GlobalContext)
  const tablesAPI = useMemo(() => new TablesAPI(), [])
  const businessAPI = useMemo(() => new BusinessAPI(), [])
  const centroCostosAPI = useMemo(() => new CentroCostosAPI(), [])
  const [data, setData] = useState([])
  const [centroCostos, setCentroCostos] = useState([])
  const [generalCentroCostoId, setGeneralCentroCostoId] = useState(null)
  const [selectedCentroCosto, setSelectedCentroCosto] = useState('')
  const [headers, setHeaders] = useState([])
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [yearFilter, setYearFilter] = useState(currentYear)

  const fixedFilters = useMemo(() => {
    const resolvedYear = Number(yearFilter) || currentYear
    return {
      fechaInicio: dayjs().year(resolvedYear).startOf('year').format('YYYY-MM-DD'),
      fechaFin: dayjs().year(resolvedYear).endOf('year').format('YYYY-MM-DD'),
    }
  }, [yearFilter])

  const resolveCentroCostoRequestParams = centroCostoValue => {
    if (!centroCostoValue) return {}

    if (centroCostoValue === UNASSIGNED_CENTER_COST_VALUE) {
      return { sinAsignar: true }
    }

    const centroCostoId = Number(centroCostoValue)
    if (!Number.isFinite(centroCostoId)) return {}

    if (generalCentroCostoId !== null && centroCostoId === Number(generalCentroCostoId)) {
      return {}
    }

    return { centroCostoId }
  }

  const centroCostoOptions = useMemo(
    () => {
      const normalizedOptions = centroCostos.map(cc => {
        const normalizedName = String(cc?.name || '').trim().toLowerCase()
        return {
          label: cc.name,
          value:
            normalizedName === 'sin asignar'
              ? UNASSIGNED_CENTER_COST_VALUE
              : String(cc.id),
        }
      })

      if (!normalizedOptions.some(option => option.value === UNASSIGNED_CENTER_COST_VALUE)) {
        normalizedOptions.push({
          label: 'Sin asignar',
          value: UNASSIGNED_CENTER_COST_VALUE,
        })
      }

      return normalizedOptions
    },
    [centroCostos]
  )

  const getData = async (centroCostoIdOverride) => {
    if (!currentBusiness?.id) return
    ui.setLoader({ visible: true, text: 'Cargando datos...' })
    try {
      const resolvedCentroCosto =
        centroCostoIdOverride === null
          ? ''
          : centroCostoIdOverride !== undefined
            ? centroCostoIdOverride
            : selectedCentroCosto

      const centroCostoParams = resolveCentroCostoRequestParams(resolvedCentroCosto)

      const { data: incoming } = await businessAPI.getEstadoResultadoExtend({
        businessId: currentBusiness.id,
        from:        fixedFilters.fechaInicio,
        to:          fixedFilters.fechaFin,
        ...centroCostoParams,
      })
      setData(incoming || [])
    } catch (e) {
      console.error(e)
      setData([])
    } finally {
      ui.setLoader({ visible: false, text: '' })
    }
  }
  const getAccountHeaders = async () => {
    if (!currentBusiness?.id) return
    try {
      const cuentas = await tablesAPI.getCuentaContable(currentBusiness.id)
      // Filtramos solo las dos cuentas finales que necesitamos
      const wantedCodes = ['3-1-0-0', '3-2-0-0']
      const filtered = cuentas.filter(c => wantedCodes.includes(c.codigoFinal))
      // Si solo necesitas el nombre final como header:
      setHeaders(filtered)
    } catch (e) {
      console.error('Error cargando cabeceras:', e)
      setHeaders([])
    }
  }

  const getCentroCostos = async () => {
    if (!currentBusiness?.id) return
    try {
      const response = await centroCostosAPI.getCentroCostos(currentBusiness.id)
      const list = response.data || []
      setCentroCostos(list)
      const general = list.find(cc => {
        if (typeof cc.name !== 'string') return false
        const name = cc.name.toLowerCase()
        return name.includes('general') || name.includes('consolidado')
      })
      setGeneralCentroCostoId(general ? general.id : null)
      if (general?.id) {
        setSelectedCentroCosto(String(general.id))
      }
    } catch (e) {
      console.error('Error cargando centros de costo:', e)
      setCentroCostos([])
    }
  }

  useEffect(() => { 
    setSelectedCentroCosto('')
    setGeneralCentroCostoId(null)
    getAccountHeaders()
    getCentroCostos()
    getData(null)
  }, [currentBusiness?.id])

  useEffect(() => {
    const options = [...yearsOptions]
    if (!options.length) return
    const hasYear = options.some(option => Number(option.value) === Number(yearFilter))
    if (!hasYear) {
      setYearFilter(currentYear)
    }
  }, [currentBusiness?.id, currentBusiness?.createdAt, yearFilter])

  useEffect(() => {
    if (!currentBusiness?.id) return
    getData()
  }, [fixedFilters.fechaInicio, fixedFilters.fechaFin, currentBusiness?.id])

  const handleCentroCostoChange = value => {
    setSelectedCentroCosto(value)
    getData(value)
  }

  const handleExport = async (email) => {
    if (!currentBusiness?.id) {
      return
    }
    setIsExporting(true)
    try {
      const centroCostoParams = resolveCentroCostoRequestParams(selectedCentroCosto)
      await businessAPI.exportEstadoResultadoExtend({
        businessId: currentBusiness.id,
        email,
        from: fixedFilters.fechaInicio,
        to: fixedFilters.fechaFin,
        ...centroCostoParams
      })
    } catch (error) {
      console.error('Error al exportar estado de resultados extendido:', error)
      throw error
    } finally {
      setIsExporting(false)
    }
  }

  const icons = [
    <button
      key='export-xlsx'
      className='w-10 h-10 text-green-700 font-bold cursor-pointer rounded-full shadow bg-white'
      onClick={() => setIsExportModalOpen(true)}
      disabled={!currentBusiness?.id}
    >
      XLS
    </button>
  ]

  return (
    <>
      <HeadingTitle
        title='Estado de Resultados Comparativo'
        rightIcons={icons}
        iconHeight={32}
      />

      <div className='p-4'>
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end'>
          <FilterControl
            label='Año'
            value={yearFilter}
            onChange={value => setYearFilter(Number(value))}
            options={yearsOptions}
            fullWidth
          />
          <FilterDateControl label='Fecha Desde' value={fixedFilters.fechaInicio} disabled />
          <FilterDateControl label='Fecha Hasta' value={fixedFilters.fechaFin} disabled />
          <FilterControl
            label='Centro de costo'
            value={selectedCentroCosto}
            onChange={handleCentroCostoChange}
            options={centroCostoOptions}
            fullWidth
          />
        </div>
      </div>
      {/* La tabla sigue recibiendo los datos crudos */}
      <TableExtend
        rows={data}
        getData={getData}
        headers={headers}
      />
      <ExportToXlsxModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExport}
        title='Exportar Estado de Resultados Comparativo'
        description='Se enviará un archivo Excel con el estado de resultados comparativo al email especificado'
        isLoading={isExporting}
      />
    </>
  )
}
